import { store } from '@/app/store';
import { getField, statisticsAclKey, UrlInfo } from '@/features/channels/channelsSlice';
import {
  canOverrideScreentime,
  formatScreenTimeDuration,
  normalizeScreentimePeriod,
  screentimePeriodValues,
} from '@/features/screentime/screenTimeApi';

export const statisticsUrl = "/statistics.json";

// Statistics has its own ACL (`enable-statistics`), separate from
// `screentimeAclKey` which stays scoped to the screen-time allowance/window
// settings. While the key is entirely absent — every project predating it —
// the grant falls back to the full screen-time grant so nothing changes on
// deploy; once written, "1" or anything else decides outright, and an
// explicit "" is a deny even where screen time is on.
export function canOverrideStatistics(acls?: UrlInfo[]): boolean {
  const list = acls || [];
  const explicit = getField(list, statisticsAclKey);

  if (explicit == "1")
    return true;

  if (explicit != "")
    return false;

  return canOverrideScreentime(list);
}

export { formatScreenTimeDuration };

export const statisticsPeriodValues = screentimePeriodValues;

// Same four periods as screen time (day/week/month/year), same "day" fallback
// for empty/unknown values — /statistics.json normalizes with the identical
// house helper (domain.NormalizeScreentimePeriod) server-side.
export const normalizeStatisticsPeriod = normalizeScreentimePeriod;

// Mirrors the server-side clamp (daily retention 200d, monthly 36mo):
// day<=199, week<=27, month<=6, year<=2 periods back from the current one.
const maxOffsetByPeriod: { [period: string]: number } = {
  day:		199,
  week:		27,
  month:	6,
  year:		2,
};

// The three metrics /statistics.json can rank rooms by; unknown/empty folds
// to "seconds" (app usage time), the server's own fallback.
export const statisticsSortValues = ["seconds", "launches", "screentime"];

export function normalizeStatisticsSort(sort?: string): string {
  return sort && statisticsSortValues.indexOf(sort) > -1 ? sort : "seconds";
}

export function clampStatisticsOffset(period?: string, offset?: number): number {
  const max = maxOffsetByPeriod[normalizeStatisticsPeriod(period)];

  if (	offset === undefined ||
		offset === null ||
		!isFinite(offset) ||
		offset < 0
	 )
    return 0;

  return Math.min(max, Math.floor(offset));
}

export interface StatisticsBucket {
  start:				number;		// unix seconds, inclusive
  days:					number;
  launches:				number;
  seconds:				number;
}

export interface ScreentimeBucket {
  start:				number;
  days:					number;
  seconds:				number;
}

export interface StatisticsTotals {
  launches:				number;
  seconds:				number;
  avg_launches_per_day:	number;
  avg_seconds_per_day:	number;
}

export interface AppTotal {
  app:					string;
  launches:				number;
  seconds:				number;
  avg_launches_per_day:	number;
  avg_seconds_per_day:	number;
}

export interface ScreentimeSummary {
  seconds:				number;
  avg_seconds_per_day:	number;
  buckets:				ScreentimeBucket[];
}

// One row of the per-room ranking — the UNION of app-usage rooms and
// screen-time rooms, so a room with one but not the other still ranks (its
// missing figures read as zero/null). `screentime_seconds` is null rather
// than 0 when the screen-time sink is off or that room's read failed — never
// let a failed screen-time read masquerade as "no screen time".
export interface RoomTotal {
  room_no:				string;
  launches:				number;
  seconds:				number;
  avg_launches_per_day:	number;
  avg_seconds_per_day:	number;
  screentime_seconds:	number | null;
}

export interface StatisticsReport {
  project_id:			string;
  room_no:				string;
  all_rooms:			boolean;
  period:				string;
  offset:				number;
  sort:					string;		// "seconds" | "launches" | "screentime" — the rooms[] ranking key
  window_from:			number;		// unix seconds, inclusive
  window_to:			number;		// unix seconds, exclusive
  window_days:			number;
  timezone:				string;
  granularity:			string;		// "day" | "month"
  totals:				StatisticsTotals;
  apps:					AppTotal[];	// sorted seconds desc, then app asc
  buckets:				StatisticsBucket[];	// zero-filled, ascending
  rooms_active:			number;		// all-rooms mode only
  rooms:				RoomTotal[];	// all-rooms mode only; ranked by `sort`, capped server-side
  rooms_total:			number;		// true room count before the cap — 0 in per-room mode
  screentime:			ScreentimeSummary | null;	// null when the sink is off
}

export type StatisticsResult =
  | { kind: "ok";					report: StatisticsReport }
  | { kind: "unauthorized" }							// 401 - session expired
  | { kind: "forbidden" }								// 403 - no project access / ACL not granted
  | { kind: "not_found" }								// 404 - project or room unknown
  | { kind: "disabled" }								// 503 - feature not enabled on this server
  | { kind: "error";				status: number; message: string };	// 400/405/500/network

export interface StatisticsRequest {
  projectId:			string;
  roomNo?:				string;		// "" or "all" (case-insensitive) = all rooms
  period?:				string;
  offset?:				number;
  sort?:				string;		// ranks rooms[]; ignored server-side in per-room mode
}

function toNumber(v: any): number {
  return typeof v === "number" && isFinite(v) ? v : 0;
}

function normalizeBucket(raw: any): StatisticsBucket {
  return {
    start:		toNumber(raw?.start),
    days:		toNumber(raw?.days),
    launches:	toNumber(raw?.launches),
    seconds:	toNumber(raw?.seconds),
  };
}

function normalizeScreentimeBucket(raw: any): ScreentimeBucket {
  return {
    start:		toNumber(raw?.start),
    days:		toNumber(raw?.days),
    seconds:	toNumber(raw?.seconds),
  };
}

function normalizeAppTotal(raw: any): AppTotal {
  return {
    app:					typeof raw?.app === "string" ? raw.app : "",
    launches:				toNumber(raw?.launches),
    seconds:				toNumber(raw?.seconds),
    avg_launches_per_day:	toNumber(raw?.avg_launches_per_day),
    avg_seconds_per_day:	toNumber(raw?.avg_seconds_per_day),
  };
}

function normalizeRoomTotal(raw: any): RoomTotal {
  return {
    room_no:				typeof raw?.room_no === "string" ? raw.room_no : "",
    launches:				toNumber(raw?.launches),
    seconds:				toNumber(raw?.seconds),
    avg_launches_per_day:	toNumber(raw?.avg_launches_per_day),
    avg_seconds_per_day:	toNumber(raw?.avg_seconds_per_day),
    screentime_seconds:	typeof raw?.screentime_seconds === "number" && isFinite(raw.screentime_seconds)
							?	raw.screentime_seconds
							:	null,
  };
}

function normalizeTotals(raw: any): StatisticsTotals {
  return {
    launches:				toNumber(raw?.launches),
    seconds:				toNumber(raw?.seconds),
    avg_launches_per_day:	toNumber(raw?.avg_launches_per_day),
    avg_seconds_per_day:	toNumber(raw?.avg_seconds_per_day),
  };
}

function normalizeScreentime(raw: any): ScreentimeSummary | null {
  if (	!raw ||
		typeof raw !== "object"
	 )
    return null;

  return {
    seconds:				toNumber(raw.seconds),
    avg_seconds_per_day:	toNumber(raw.avg_seconds_per_day),
    buckets:				Array.isArray(raw.buckets) ? raw.buckets.map(normalizeScreentimeBucket) : [],
  };
}

function normalizeReport(body: any): StatisticsReport {
  return {
    project_id:		typeof body?.project_id === "string" ? body.project_id : "",
    room_no:		typeof body?.room_no    === "string" ? body.room_no    : "",
    all_rooms:		body?.all_rooms === true,
    period:			normalizeStatisticsPeriod(body?.period),
    offset:			toNumber(body?.offset),
    sort:			normalizeStatisticsSort(body?.sort),
    window_from:	toNumber(body?.window_from),
    window_to:		toNumber(body?.window_to),
    window_days:	Math.max(1, toNumber(body?.window_days) || 1),
    timezone:		typeof body?.timezone === "string" ? body.timezone : "",
    granularity:	body?.granularity === "month" ? "month" : "day",
    totals:			normalizeTotals(body?.totals),
    apps:			Array.isArray(body?.apps) ? body.apps.map(normalizeAppTotal) : [],
    buckets:		Array.isArray(body?.buckets) ? body.buckets.map(normalizeBucket) : [],
    rooms_active:	toNumber(body?.rooms_active),
    rooms:			Array.isArray(body?.rooms) ? body.rooms.map(normalizeRoomTotal) : [],
    rooms_total:	toNumber(body?.rooms_total),
    screentime:		normalizeScreentime(body?.screentime),
  };
}

export async function fetchStatistics(req: StatisticsRequest): Promise<StatisticsResult> {
  const params = new URLSearchParams();

  params.set("project_id", req.projectId);

  if (	req.roomNo &&
		req.roomNo.trim() !== "" &&
		req.roomNo.trim().toLowerCase() !== "all"
	 )
    params.set("room_no", req.roomNo.trim());

  const period = normalizeStatisticsPeriod(req.period);
  params.set("period", period);
  params.set("offset", clampStatisticsOffset(period, req.offset).toString());
  params.set("sort", normalizeStatisticsSort(req.sort));

  let response: Response;

  try {
    response = await fetch(`${statisticsUrl}?${params.toString()}`, {
      method:		"GET",
      credentials:	"include",
    });
  } catch (e) {
    console.error("fetchStatistics/failed:", e);
    return { kind: "error", status: 0, message: "Network error" };
  }

  if (response.ok) {
    try {
      return { kind: "ok", report: normalizeReport(await response.json()) };
    } catch (e) {
      console.error("fetchStatistics/malformed body:", e);
      return { kind: "error", status: response.status, message: "Malformed response" };
    }
  }

  let text = "";
  try {
    text = (await response.text()).trim();
  } catch (_e) { /* body already consumed or empty */ }

  switch (response.status) {
  case 401:
    store.dispatchStatus(0);
    return { kind: "unauthorized" };

  case 403:
    return { kind: "forbidden" };

  case 404:
    return { kind: "not_found" };

  case 503:
    return { kind: "disabled" };

  default:
    return {
      kind:		"error",
      status:	response.status,
      message:	text || response.statusText || `Request failed (${response.status})`,
    };
  }
}
